import React, { useEffect, useState } from 'react'
import { Card, Button, Row, Col, Spin, Table } from 'antd'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { wtApiCall } from 'src/helpers/commons/ApiWrapper';
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import WidgetPartySelector from 'src/widgets/Selectors/WidgetPartySelector'
import BaseListing from 'src/BaseListings'
import * as XLSX from 'xlsx'

const PartiesReport = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [parties, setParties] = useState([])
  const [partyId, setPartyId] = useState(null)
  const [partyName, setPartyName] = useState('')
  const [reportData, setReportData] = useState([])

  const Base = new BaseListing('party_report')

  const companyName = wtGetFromLocalStorage('company_name')

  const changeValues = (key, val) => {
    if (key === 'party_id') {
      setPartyId(val || null)

      const selectedParty = parties.find(p => p.value === val)
      setPartyName(selectedParty?.label || '')
    }
  }

  const columns = [
    ['Party ID#', 'id', 90, true],
    ['Code', 'code', 100, true],
    ['Name', 'name', 220, true],
    ['Type', 'type', 110, true],
    ['TaxPayer Status', 'taxpayer_status', 150],
    ['Filer', 'filer', 80],
    ['Related', 'related', 90],
    ['Email', 'email', 220],
    ['Contact Person', 'contact_person', 170],
    ['NTN', 'ntn', 130],
    ['Full NTN', 'full_ntn', 150],
    ['CNIC', 'cnic', 150],
    ['City', 'city', 130],
    ['Country', 'country', 130],
    ['State', 'state', 130],
    ['Phone 1', 'phone_1', 140],
    ['Phone 2', 'phone_2', 140],
    ['Shipping Address', 'shipping_address', 280],
    ['Billing Address', 'billing_address', 280],
    ['Credit Limit', 'credit_limit', 130],
    ['Status', 'status', 110],
    ['Lock Status', 'approval_status', 120],
    ['Retail Customer', 'retail_customer', 150],
    ['Government Client', 'is_government_client', 170],
    ['Dealer', 'is_dealer', 100],
    ['License Number', 'license_number', 160],
    ['Created By', 'created_by', 180],
    ['Created At', 'created_at', 180],
  ].map(([title, dataIndex, width, fixed]) => ({
    title,
    dataIndex,
    key: dataIndex,
    width,
    ...(fixed ? { fixed: 'left' } : {}),
  }))


  const exportToExcel = () => {
    const exportColumns = columns.map(({ title, dataIndex }) => ({
      label: typeof title === 'string' ? title : dataIndex,
      value: dataIndex,
    }))
    const rows = reportData.map((record) => Object.fromEntries(
      exportColumns.map(({ label, value }) => [label, record[value] ?? '']),
    ))
    const sheet = XLSX.utils.json_to_sheet(rows)
    const excelColumnWidths = {
      id: 12,
      code: 12,
      name: 28,
      type: 14,
      taxpayer_status: 20,
      filer: 10,
      related: 10,
      email: 30,
      contact_person: 24,
      ntn: 18,
      full_ntn: 18,
      cnic: 18,
      city: 18,
      country: 18,
      state: 18,
      phone_1: 18,
      phone_2: 18,
      shipping_address: 40,
      billing_address: 40,
      credit_limit: 16,
      status: 14,
      approval_status: 16,
      retail_customer: 18,
      is_government_client: 22,
      is_dealer: 12,
      license_number: 20,
      created_by: 24,
      created_at: 22,
    }
    sheet['!cols'] = exportColumns.map(({ value, label }) => ({
      wch: excelColumnWidths[value] || Math.max(String(label).length + 2, 14),
    }))
    sheet['!autofilter'] = { ref: `A1:${XLSX.utils.encode_col(exportColumns.length - 1)}${rows.length + 1}` }
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, sheet, 'Parties Report')
    XLSX.writeFile(workbook, 'parties_report.xlsx')
  }

  const getReportData = () => {
    setIsLoading(true)

    wtApiCall(
      'reports/parties_report',
      {
        party_id: partyId
      },
      'get',
      (response) => {
        if (response && response.data) {
          setReportData(response.data || [])
        } else {
          setReportData([])
        }
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      }
    )
  }


  useEffect(() => {
    Base.dropdownData('parties', {}, [setParties], 1)
  }, [])


  return (
    <div
      className="container-fluid"
      style={{
        padding: '1em',
        backgroundColor: 'white',
        minHeight: '100vh',
        fontFamily: 'Helvetica',
      }}
    >
      <Card
        title={
          <span style={{ fontSize: '22px', marginLeft: '40%' }}>
            {companyName + ' Parties Report'}
          </span>
        }
        style={{ width: '100%' }}
      >
        <Row gutter={[24, 24]}>

          <Col xs={15} sm={10} md={6}>
            <div className="form-group">
              <label>Party Name</label>
              <WidgetPartySelector
                onChange={(value) => changeValues('party_id', value)}
                placeholder="Select a party"
                parties={parties}
                displayValue={partyName}
                value={partyId}
                reloadDropdownData={() => Base.dropdownData('parties', {}, [setParties], 1)}
              />
            </div>
          </Col>

          <Col xs={12} sm={8} md={5}>
            <Button
              style={{ marginTop: 36 }}
              type="primary"
              onClick={getReportData}
              disabled={isLoading}
            >
              Generate Report
            </Button>
            <Button
              style={{ marginTop: 36, backgroundColor: "#4BB543", color: "#fff", marginLeft: 10 }}
              onClick={exportToExcel}
              disabled={isLoading || reportData.length === 0}
            >
              Export Excel
            </Button>
          </Col>
        </Row>

        <br />
        <div style={{ width: '100%' }}>
          <Table
            columns={columns}
            dataSource={reportData}
            pagination={false}
            bordered
            size="small"
            scroll={{ x: 4200, y: 500 }}
            rowKey={(record, index) => index}
            loading={{ indicator: <Spin />, spinning: isLoading }}
          />
        </div>
      </Card>
    </div>
  )
}

export default PartiesReport
